/**
 * ARForm Zod Validation Schema
 * Validates all ARForm fields against the PWA parity source.
 */

import { z } from 'zod'

// ============================================================================
// Enum Schemas
// ============================================================================

export const hazardSeveritySchema = z.enum(['minor', 'moderate', 'severe'])

// Friendly form-facing message for the bare-required Detailed usage (Rapid adds its own .refine
// message); without it, a missing posting shows Zod's generic 'Invalid option: expected one of ...'.
export const postingStatusSchema = z.enum(['inspected', 'restricted_use', 'unsafe'], {
  error: 'Please select a posting status',
})

export const damagePercentageSchema = z.enum(['none', '0_1', '1_10', '10_30', '30_60', '60_100', '100'])

export const areasInspectedSchema = z.enum(['exterior_only', 'exterior_and_interior'])

export const constructionTypeSchema = z.enum([
  'wood_frame',
  'steel_frame',
  'concrete_frame',
  'concrete_shear_wall',
  'unreinforced_masonry',
  'reinforced_masonry',
  'tilt_up',
  'other',
])

export const occupancyTypeSchema = z.enum([
  'residential',
  'other_residential',
  'commercial',
  'offices',
  'industrial',
  'government',
  'school',
  'hospital',
  'historic',
  'public_assembly',
  'other',
])

export const detailedEvalTypeSchema = z.enum(['structural', 'geotechnical', 'other'])

/**
 * A text field the assessments API lists in its create-handler `requiredFields` and that the
 * client enforces rather than letting the save fail at the boundary.
 *
 * That handler counts `undefined`, `null` and `""` alike as missing and answers
 * 400 `assessments.post.badrequest.missingfields`, so a value the client treats as
 * optional becomes a rejected save the user cannot diagnose. Whitespace is trimmed
 * before the length check because a space-only value clears the API's `=== ""` test
 * and would persist as blank.
 *
 * Not every field on the API's list uses this -- `parcel_id`, `latitude` and `longitude` are
 * optional by product decision and the API checks on them are being relaxed to match.
 *
 * Shared with ADForm.schema.ts -- both forms POST to the same endpoint.
 */
export const apiRequiredText = (message: string) =>
  z.string({ error: message }).trim().min(1, message)

// ============================================================================
// Complex Object Schemas
// ============================================================================

/**
 * Hazard assessment schema
 * 5 core hazards are required, "other" is optional
 */
export const hazardAssessmentSchema = z
  .object({
    collapseOrOffFoundation: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    buildingOrStoryLeaning: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    rackingDamageWalls: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    chimneyParapetFallingHazard: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    groundSlopeMovement: hazardSeveritySchema
      .optional()
      .refine((val) => val !== undefined, { message: 'Please select a severity' }),
    otherDescription: z.string().optional(),
    otherSeverity: hazardSeveritySchema.optional(),
  })
  .refine(
    (data) => {
      // If otherDescription is provided, otherSeverity should also be provided
      if (data.otherDescription && data.otherDescription.trim() !== '') {
        return data.otherSeverity !== undefined
      }
      return true
    },
    {
      message: 'Severity is required when "Other" hazard description is provided',
      path: ['otherSeverity'],
    }
  )

/**
 * Assessment photo schema
 */
export const assessmentPhotoSchema = z.object({
  id: z.string(),
  uri: z.string(),
  timestamp: z.string(),
  caption: z.string().optional(),
})

// ============================================================================
// Main Form Schema
// ============================================================================

/**
 * Complete ARForm validation schema.
 * Required-ness per field is defined inline below (PWA parity).
 */
export const arFormSchema = z.object({
  // ============================================
  // Metadata
  // ============================================
  id: z.string().optional(),
  // Deliberately not required: an assessment may be recorded without a parcel (it just will not
  // appear on the map). The API still lists `parcel_id` in its create-handler `requiredFields`;
  // that check is being relaxed to match. Until it is, a parcel-less save answers 400.
  parcelId: z.string(),

  // ============================================
  // Section 1: Inspection (4 fields)
  // ============================================
  inspectorId: z.string().min(1, 'Inspector ID is required'),
  affiliation: z.string().optional(),
  inspectionDateTime: z.string().min(1, 'Inspection date/time is required'),
  areasInspected: areasInspectedSchema
    .optional()
    .refine((val) => val !== undefined, { message: 'Please select areas inspected' }),

  // ============================================
  // Section 2: Building (11 fields)
  // ============================================
  // Address fields are optional to match the PWA building step (construction + occupancy only).
  // County is the exception: the API lists it in `requiredFields` and rejects a blank one.
  // `latitude` / `longitude` are on that list too but stay optional here by product decision,
  // alongside `parcelId` -- the API checks on all three are being relaxed to match.
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  county: apiRequiredText('County is required'),
  state: z.string().optional(),
  zipcode: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  typeOfConstruction: constructionTypeSchema
    .optional()
    .refine((val) => val !== undefined, { message: 'Please select a construction type' }),
  constructionTypeOther: z.string().optional(),
  primaryOccupancy: occupancyTypeSchema
    .optional()
    .refine((val) => val !== undefined, { message: 'Please select a primary occupancy' }),
  occupancyTypeOther: z.string().optional(),
  buildingName: z.string().optional(),
  contactPhone: z.string().optional(),
  storiesAboveGround: z.string().optional(),
  storiesBelowGround: z.string().optional(),
  buildingFootprint: z.string().optional(),
  residentialUnits: z.string().optional(),
  inhabitableResidentialUnits: z.string().optional(),

  // ============================================
  // Section 3: Evaluation (3 fields)
  // ============================================
  hazards: hazardAssessmentSchema,
  // Optional in Rapid (PWA evaluation step does not block on damage estimate).
  estimatedBuildingDamage: damagePercentageSchema.optional(),
  comments: z.string().optional(),

  // ============================================
  // Section 4: Posting (4 fields)
  // ============================================
  // Required to match the PWA posting step (a placard must be chosen, not NOT_ASSESSED).
  postingStatus: postingStatusSchema
    .optional()
    .refine((val) => val !== undefined, { message: 'Please select a posting status' }),
  restrictionsText: z.string().optional(),
  detailedEvalRecommended: z.boolean(),
  detailedEvalTypes: z.array(detailedEvalTypeSchema).optional(),

  // ============================================
  // Section 5: Further Actions
  // ============================================
  barricadesNeeded: z.boolean().optional(),
  barricadeLocations: z.string().optional(),
  engineeringEvalRecommended: z.boolean().optional(),
  engineeringEvalStructural: z.boolean().optional(),
  engineeringEvalGeotechnical: z.boolean().optional(),
  engineeringEvalOther: z.string().optional(),
  actionsComments: z.string().optional(),

  // ============================================
  // Section 6: Image (1 field)
  // ============================================
  photos: z.array(assessmentPhotoSchema).optional(),
})

/**
 * Inferred TypeScript type from the schema
 */
export type ARFormSchemaType = z.infer<typeof arFormSchema>
